import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Loader2, ArrowDownCircle, ArrowUpCircle, Filter, Clock, Calendar, Users } from 'lucide-react';

/**
 * Item retornado pelo backend (listCreditHistory).
 * Espelha CreditHistoryItem em functions/src/creditHistory.ts.
 */
interface CreditHistoryItem {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  type: 'GRANT' | 'DEBIT' | 'REVOKE';
  billingType: string; // "HOURS" | "SESSIONS"
  sessionLabel?: string | null;
  amount: number;
  createdAt: number;
  source: { kind: string; id: string; subscriptionId?: string };
  displayTitle?: string;
  clientName?: string;
  appointmentId?: string;
  meetingId?: string;
  isSubscription?: boolean;
  aggregatedEntryCount?: number;
  sessionStartedAt?: number;
  sessionEndedAt?: number;
}

interface CreditHistoryResponse {
  items: CreditHistoryItem[];
  hasMore: boolean;
  nextCursor?: number;
  totals: {
    grantedHours: number;
    debitedHours: number;
    grantedSessions: number;
    debitedSessions: number;
    transactionCount: number;
  };
  currentBalance: {
    hours: number;
    sessionsConstellation: number;
    sessionsParticipation: number;
  };
}

interface Props {
  /**
   * Quando definido, força a consulta para este UID (modo admin consultando outro usuário).
   * Quando indefinido, o backend usa o próprio usuário autenticado.
   */
  targetUserId?: string;
  /**
   * Mostra a coluna "Usuário" e o e-mail (modo admin).
   */
  showOwnerColumn?: boolean;
  /**
   * Título exibido no topo (default: "Histórico de uso de créditos").
   */
  title?: string;
}

const PERIOD_OPTIONS = [
  { id: 'all', label: 'Tudo' },
  { id: '7', label: 'Últimos 7 dias' },
  { id: '30', label: 'Últimos 30 dias' },
  { id: '90', label: 'Últimos 90 dias' },
] as const;

type PeriodId = typeof PERIOD_OPTIONS[number]['id'];
type TypeFilter = 'ALL' | 'GRANT' | 'DEBIT' | 'REVOKE';

const formatDate = (epochMs: number): string => {
  const date = new Date(epochMs);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatHoursBalanceLabel = (availableHours: number): string => {
  const totalMinutes = Math.round(availableHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatAmount = (item: CreditHistoryItem): string => {
  const abs = Math.abs(item.amount);
  const sign = item.type === 'DEBIT' || item.type === 'REVOKE' ? '−' : '+';
  if (item.billingType === 'HOURS') {
    // Mostra horas com até 2 casas; minutos para valores fracionários
    if (abs < 1 && abs > 0) {
      const minutes = Math.round(abs * 60);
      return `${sign}${minutes} min`;
    }
    const rounded = Math.round(abs * 100) / 100;
    return `${sign}${rounded.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h`;
  }
  // SESSIONS
  const label = abs === 1 ? 'sessão' : 'sessões';
  return `${sign}${abs} ${label}`;
};

const formatSessionPeriod = (item: CreditHistoryItem): string | null => {
  if (!item.sessionStartedAt || !item.sessionEndedAt) return null;
  if (item.sessionStartedAt === item.sessionEndedAt) return null;
  const start = formatDate(item.sessionStartedAt);
  const end = formatDate(item.sessionEndedAt);
  return `${start} → ${end}`;
};

const sourceKindBadge = (kind: string): { label: string; classes: string } => {
  const map: Record<string, { label: string; classes: string }> = {
    SESSION_TIME: { label: 'Tempo em sala', classes: 'bg-rose-100 text-rose-700' },
    CONSTELLATION_APPOINTMENT: { label: 'Constelação', classes: 'bg-purple-100 text-purple-700' },
    APPOINTMENT_PARTICIPATION: { label: 'Participação', classes: 'bg-blue-100 text-blue-700' },
    ROOM_PARTICIPATION: { label: 'Sala', classes: 'bg-cyan-100 text-cyan-700' },
    ASAAS_CHARGE: { label: 'Compra avulsa', classes: 'bg-emerald-100 text-emerald-700' },
    ASAAS_SUBSCRIPTION: { label: 'Assinatura', classes: 'bg-amber-100 text-amber-700' },
    WELCOME_TRIAL: { label: 'Bônus de boas-vindas', classes: 'bg-emerald-100 text-emerald-700' },
    ADMIN_ADJUSTMENT: { label: 'Ajuste admin', classes: 'bg-emerald-100 text-emerald-700' },
    LEGACY_BALANCE: { label: 'Saldo anterior', classes: 'bg-sky-100 text-sky-700' },
    SUBSCRIPTION_REVOKE: { label: 'Assinatura', classes: 'bg-orange-100 text-orange-700' },
  };
  return map[kind] || { label: kind, classes: 'bg-gray-100 text-gray-700' };
};

const rowTypeLabel = (item: CreditHistoryItem): { isNegative: boolean; badgeLabel?: string } => {
  if (item.type === 'REVOKE') {
    return { isNegative: true, badgeLabel: 'Revogação' };
  }
  return { isNegative: item.type === 'DEBIT' };
};

const CreditHistoryTable: React.FC<Props> = ({ targetUserId, showOwnerColumn, title }) => {
  const [items, setItems] = useState<CreditHistoryItem[]>([]);
  const [totals, setTotals] = useState<CreditHistoryResponse['totals']>({
    grantedHours: 0,
    debitedHours: 0,
    grantedSessions: 0,
    debitedSessions: 0,
    transactionCount: 0,
  });
  const [currentBalance, setCurrentBalance] = useState<CreditHistoryResponse['currentBalance']>({
    hours: 0,
    sessionsConstellation: 0,
    sessionsParticipation: 0,
  });
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [period, setPeriod] = useState<PeriodId>('30');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');

  const dateRange = useMemo(() => {
    if (period === 'all') return { startDate: undefined as number | undefined };
    const days = parseInt(period, 10);
    const startDate = Date.now() - days * 24 * 60 * 60 * 1000;
    return { startDate };
  }, [period]);

  const fetchHistory = useCallback(
    async (append: boolean) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }
        setError(null);

        const callable = httpsCallable<any, CreditHistoryResponse>(functions, 'listCreditHistory');
        const result = await callable({
          ...(targetUserId ? { userId: targetUserId } : {}),
          ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
          type: typeFilter,
          limit: 50,
          ...(append && nextCursor ? { cursorBeforeMs: nextCursor } : {}),
        });

        const response = result.data;
        if (append) {
          setItems((prev) => [...prev, ...response.items]);
        } else {
          setItems(response.items);
        }
        setHasMore(response.hasMore);
        setNextCursor(response.nextCursor);
        if (!append) {
          setTotals(response.totals);
          setCurrentBalance(response.currentBalance ?? {
            hours: 0,
            sessionsConstellation: 0,
            sessionsParticipation: 0,
          });
        }
      } catch (err: any) {
        console.error('Erro ao carregar histórico de créditos:', err);
        setError(err?.message || 'Não foi possível carregar o histórico');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [targetUserId, dateRange.startDate, typeFilter, nextCursor]
  );

  // Refetch ao mudar filtros (resetando cursor)
  useEffect(() => {
    setNextCursor(undefined);
    fetchHistory(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId, period, typeFilter]);

  return (
    <div className="space-y-4">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          {title}
        </h3>
      )}

      {/* Cards de totais */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 col-span-2 lg:col-span-1">
          <div className="text-xs font-medium text-sky-700 uppercase tracking-wide">Saldo atual</div>
          <div className="text-xl font-bold text-sky-900 mt-1">
            {formatHoursBalanceLabel(currentBalance.hours)}
          </div>
          <div className="text-xs text-sky-700 mt-1">
            {currentBalance.sessionsConstellation} const. · {currentBalance.sessionsParticipation} part.
          </div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">
            Horas creditadas{period !== 'all' ? ' (período)' : ''}
          </div>
          <div className="text-xl font-bold text-emerald-900 mt-1">
            +{totals.grantedHours.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h
          </div>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
          <div className="text-xs font-medium text-rose-700 uppercase tracking-wide">
            Horas consumidas{period !== 'all' ? ' (período)' : ''}
          </div>
          <div className="text-xl font-bold text-rose-900 mt-1">
            −{totals.debitedHours.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h
          </div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">Sessões creditadas</div>
          <div className="text-xl font-bold text-blue-900 mt-1">+{totals.grantedSessions}</div>
        </div>
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
          <div className="text-xs font-medium text-purple-700 uppercase tracking-wide">Sessões consumidas</div>
          <div className="text-xl font-bold text-purple-900 mt-1">−{totals.debitedSessions}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <Filter className="w-4 h-4 text-gray-500" />
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 font-medium">Período:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodId)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 font-medium">Tipo:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="ALL">Todos</option>
            <option value="GRANT">Apenas compras/recebimentos</option>
            <option value="DEBIT">Apenas consumos</option>
            <option value="REVOKE">Apenas revogações (atraso)</option>
          </select>
        </div>
        <p className="text-xs text-gray-500 w-full sm:w-auto">
          Débitos de tempo em sala são agrupados por reunião (soma correta das horas).
          {period !== 'all' && currentBalance.hours > 0 && (
            <> O saldo atual inclui créditos concedidos fora do período.</>
          )}
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Carregando histórico...</span>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">Nenhuma movimentação no período selecionado.</p>
        </div>
      ) : (
        <>
          {/* Tabela desktop */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Data</th>
                  {showOwnerColumn && <th className="px-4 py-3 font-semibold text-gray-700">Usuário</th>}
                  <th className="px-4 py-3 font-semibold text-gray-700">Tipo</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Descrição</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Cliente / Origem</th>
                  <th className="px-4 py-3 font-semibold text-gray-700 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const badge = sourceKindBadge(item.source.kind);
                  const { isNegative, badgeLabel } = rowTypeLabel(item);
                  const sessionPeriod = formatSessionPeriod(item);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(item.createdAt)}
                        {sessionPeriod && (
                          <div className="text-xs text-gray-400 mt-0.5">{sessionPeriod}</div>
                        )}
                      </td>
                      {showOwnerColumn && (
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{item.userName || '—'}</div>
                          {item.userEmail && (
                            <div className="text-xs text-gray-500">{item.userEmail}</div>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                          {isNegative ? <ArrowDownCircle className="w-3 h-3" /> : <ArrowUpCircle className="w-3 h-3" />}
                          {badgeLabel || badge.label}
                        </span>
                        {item.isSubscription && (
                          <span className="ml-1 text-xs text-amber-700">(recorrente)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {item.displayTitle || '—'}
                        {item.aggregatedEntryCount && item.aggregatedEntryCount > 1 && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.aggregatedEntryCount} lançamentos agrupados
                          </div>
                        )}
                        {item.appointmentId && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            #{item.appointmentId.substring(0, 8)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {item.clientName ? (
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3.5 h-3.5 text-gray-400" />
                            {item.clientName}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${isNegative ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {formatAmount(item)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Cards mobile */}
          <div className="md:hidden space-y-3">
            {items.map((item) => {
              const badge = sourceKindBadge(item.source.kind);
              const { isNegative, badgeLabel } = rowTypeLabel(item);
              const sessionPeriod = formatSessionPeriod(item);
              return (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                      {isNegative ? <ArrowDownCircle className="w-3 h-3" /> : <ArrowUpCircle className="w-3 h-3" />}
                      {badgeLabel || badge.label}
                    </span>
                    <span className={`font-semibold ${isNegative ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {formatAmount(item)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-900">{item.displayTitle || '—'}</div>
                  {item.aggregatedEntryCount && item.aggregatedEntryCount > 1 && (
                    <div className="text-xs text-gray-500 mt-1">
                      {item.aggregatedEntryCount} lançamentos agrupados
                    </div>
                  )}
                  {sessionPeriod && (
                    <div className="text-xs text-gray-500 mt-1">{sessionPeriod}</div>
                  )}
                  {item.clientName && (
                    <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {item.clientName}
                    </div>
                  )}
                  {showOwnerColumn && item.userName && (
                    <div className="text-xs text-gray-500 mt-1">Usuário: {item.userName}</div>
                  )}
                  <div className="text-xs text-gray-400 mt-2">{formatDate(item.createdAt)}</div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => fetchHistory(true)}
                disabled={isLoadingMore}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait flex items-center gap-2"
              >
                {isLoadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CreditHistoryTable;
